import { Auth } from "@aws-amplify/auth"
import { User } from "@app/core/dto/user/user"
import { CollaboratorTypeEnum } from "@app/core/enum/user/collaboratorTypeEnum"
import { isAtLeastSuperUser, OrganizationRoleEnum } from "@app/core/enum/user/organization-role-enum"
import { getEnvProperty, PROPERTIES } from "@app/core/services/environment-service"

/**
 *
 * Every AWS Amplify error code
 * InternalErrorException
 * InvalidLambdaResponseException
 * InvalidParameterException
 * InvalidSmsRoleAccessPolicyException
 * InvalidSmsRoleTrustRelationshipException
 * InvalidUserPoolConfigurationException
 * NotAuthorizedException
 * PasswordResetRequiredException
 * ResourceNotFoundException
 * TooManyRequestsException
 * UnexpectedLambdaException
 * UserLambdaValidationException
 * UserNotConfirmedException
 * UserNotFoundException
 */

export function hasSuperUserRights(user: User | undefined): boolean {
  return isAtLeastSuperUser(user?.role)
}

export function isMegaUser(user: User | undefined): boolean {
  return user?.role ? user.role === OrganizationRoleEnum.MEGA_USER : false
}

export function isUltimateUser(user: User | undefined): boolean {
  return user?.role ? user.role === OrganizationRoleEnum.ULTIMATE_USER : false
}

export function isNormalUser(user: User | undefined): boolean {
  return user?.role ? user.role === OrganizationRoleEnum.NORMAL_USER : false
}

export function collaboratorTypeToLabel(type: CollaboratorTypeEnum): string {
  if (type) {
    switch (type) {
      case CollaboratorTypeEnum.CREATED:
        return "Acceptée"
      case CollaboratorTypeEnum.WAITING_CREATION:
        return "Invitation en attente"
      default:
        return ""
    }
  } else {
    return ""
  }
}

export function getJwtTokenFromStorage(): string | undefined {
  return getFromStorage("idToken")
}

export function getCognitoUserIdFromStorage(): string | undefined {
  return getFromStorage("cognitoUserId")
}

export function getUserFromStorage(): User | undefined {
  const item = getFromStorage("user")
  return item ? JSON.parse(item) : undefined
}

const EXPIRED_COOKIE_DATE = "expires=Thu, 01 Jan 1970 00:00:01 GMT"

/**
 * Expires every cookie of the site the page can see (Cognito tokens, device keys, Crisp session…).
 *
 * A browser only drops a cookie when the expiring write targets the exact domain and path the cookie was created
 * with. Amplify's cookieStorage writes them on VITE_APP_COOKIE_STORAGE_DOMAIN with path "/", so the expiring write
 * is repeated for that domain (with and without the leading dot) and as a host-only cookie. Writing with no
 * attribute at all — the historical behaviour — never matched them, which let stale sessions pile up until nginx
 * rejected every request with "Request Header Or Cookie Too Large".
 */
export function deleteAllCookies(): void {
  console.info("delete all cookies")
  const cookieDomain = getEnvProperty(PROPERTIES.VITE_APP_COOKIE_STORAGE_DOMAIN)
  for (const name of getCookieNames(document.cookie)) {
    console.info("clear cookie", name)
    for (const expiredCookie of buildExpiredCookieStrings(name, cookieDomain)) {
      document.cookie = expiredCookie
    }
  }
}

/** Cookie names exactly as the browser stores them (not decoded: the expiring write must use the same name). */
export function getCookieNames(cookieHeader: string): string[] {
  return cookieHeader
    .split(";")
    .map((cookie) => cookie.substring(0, cookie.indexOf("=")).trim())
    .filter((name) => name !== "")
}

/** One expiring write per domain/path combination the application may have used for the cookie. */
export function buildExpiredCookieStrings(name: string, cookieDomain: string | undefined): string[] {
  const domains: (string | undefined)[] = [undefined]
  if (cookieDomain) {
    const bareDomain = cookieDomain.replace(/^\./, "")
    domains.push(bareDomain, `.${bareDomain}`)
  }
  return domains.map((domain) => `${name}=;${EXPIRED_COOKIE_DATE};path=/${domain ? `;domain=${domain}` : ""}`)
}

function getFromStorage(key: string): string | undefined {
  const dataFromStorage: string | null = localStorage.getItem(key)
  return dataFromStorage ?? undefined
}

export function getCognitoId(userAttributes: any): string | undefined {
  return userAttributes?.sub === undefined ? "" : userAttributes.sub
}

export function signIn(name: string, password: string): Promise<any> {
  // For possible error codes list, go to : https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_InitiateAuth.html#API_InitiateAuth_Errors
  return Auth.signIn(name, password)
}

export async function signUp(userDto: User, organization: string, password: string): Promise<any> {
  let phoneNumber = userDto.phone
  if (phoneNumber.startsWith("0")) {
    phoneNumber = phoneNumber.replace("0", "")
  }

  phoneNumber = `+33${phoneNumber}`

  return Auth.signUp({
    username: userDto.email,
    password,
    attributes: {
      email: userDto.email,
      family_name: userDto.familyName,
      given_name: userDto.givenName,
      phone_number: phoneNumber,
      "custom:organizationId": userDto.organizationId,
    },
  })
}

export function confirm(username: string, code: string): Promise<any> {
  // For possible error codes list, go to :
  // https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ConfirmSignUp.html
  return Auth.confirmSignUp(username, code)
}

export async function resendCode(username: string): Promise<any> {
  try {
    await Auth.resendSignUp(username)
    console.info("code resent successfully")
  } catch (err) {
    console.info("error resending code: ", err)
  }
}

export function completePasswordChallenge(userInfos: any, password: string): Promise<any> {
  return Auth.completeNewPassword(userInfos, password)
}

export function forgotPassword(userInfos: string): Promise<any> {
  return Auth.forgotPassword(userInfos)
}

export function forgotPasswordSubmit(userInfos: string, code: string, newPassword: string): Promise<any> {
  return Auth.forgotPasswordSubmit(userInfos, code, newPassword)
}

export function getCookieValue(name: string): string | undefined {
  const cookies = decodeURIComponent(document.cookie)
  const cookiesArray: string[] = cookies.split(";")
  for (const cookie of cookiesArray) {
    if (cookie.indexOf(`${name}=`) !== -1) {
      return cookie.substring(cookie.indexOf("=") + 1, cookie.length)
    }
  }
  return undefined
}
